import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ARCHETYPES } from './archetypeData';
import { musicVolume, getStoredAudioMode } from '../../../utils/modeAudio';
import sonicCues from '../Premium/sonicCues';

const AffirmationScreen = ({ archetypeId, musicTrack, onContinue }) => {
  const archetype = ARCHETYPES[archetypeId];
  const affirmations = archetype?.affirmations || [];
  const [visibleCount, setVisibleCount] = useState(0);
  const [allVisible, setAllVisible] = useState(false);
  const audioRef = useRef(null);

  // Continue playing music from immersion - DON'T stop it here.
  useEffect(() => {
    if (audioRef.current && musicTrack) {
      audioRef.current.src = musicTrack.url;
      audioRef.current.volume = musicVolume(getStoredAudioMode());
      audioRef.current.loop = true;
      audioRef.current.play().catch(() => {});
    }
  }, [musicTrack]);

  // Reveal affirmations one by one.
  useEffect(() => {
    if (visibleCount < affirmations.length) {
      const timer = setTimeout(() => {
        sonicCues.tap();
        setVisibleCount((prev) => prev + 1);
      }, 2500);
      return () => clearTimeout(timer);
    } else if (visibleCount === affirmations.length) {
      setTimeout(() => {
        sonicCues.threshold();
        setAllVisible(true);
      }, 1000);
    }
  }, [visibleCount, affirmations.length]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{
        background:
          "radial-gradient(ellipse at 50% 0%, #18213A 0%, #0a1224 50%, #03060A 100%)",
      }}
      data-testid="affirmation-screen"
    >
      <audio ref={audioRef} />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="text-center pt-14 pb-6 px-6"
      >
        <p
          className="uppercase tracking-[0.32em] mb-3"
          style={{
            color: "#D7BB7A",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
          }}
        >
          Threshold · 3 of 5
        </p>
        <h1
          className="text-3xl font-light italic mb-2"
          style={{
            color: "#F3EDDD",
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            letterSpacing: "0.04em",
          }}
        >
          This is now yours
        </h1>
        <p
          className="italic"
          style={{
            color: "rgba(243, 237, 221, 0.55)",
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.95rem",
          }}
        >
          Read and feel each one.
        </p>
      </motion.div>

      {/* Affirmations */}
      <div className="flex-1 flex flex-col justify-center items-center px-8 overflow-hidden">
        <AnimatePresence>
          {affirmations.slice(0, visibleCount).map((affirmation, index) => (
            <motion.p
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-6 italic"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.35rem",
                lineHeight: 1.55,
                color:
                  index === visibleCount - 1
                    ? "#F3EDDD"
                    : "rgba(243, 237, 221, 0.45)",
              }}
            >
              {affirmation}
            </motion.p>
          ))}
        </AnimatePresence>
      </div>

      {/* Subtle archetype glow */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
        style={{
          background: `linear-gradient(to top, ${archetype?.glowColor || 'rgba(215, 187, 122, 0.2)'} 0%, transparent 100%)`,
        }}
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 4, repeat: Infinity }}
      />

      {/* Continue chip — appears after all affirmations */}
      <AnimatePresence>
        {allVisible && (
          <div className="flex-shrink-0 p-6 pb-28 relative z-10 flex justify-center">
            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => {
                sonicCues.bloom();
                onContinue();
              }}
              className="rec-chip"
              data-active="true"
              data-testid="affirmation-continue-btn"
              style={{ padding: "0.85rem 2rem", fontSize: "0.66rem" }}
            >
              Continue →
            </motion.button>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AffirmationScreen;
